import { Input } from '@latitude/input';
import { FormComponentWrapper } from 'components/FormComponentWrapper';
import noop from 'lodash.noop';
import { useId } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { FormInputProps } from './types';

export const FormInput = <T extends FieldValues = FieldValues>({
  name,
  control,
  description,
  label,
  wrapperClassname,
  disabled,
  message,
  optional,
  rules,
  hideError,
  maxLength,
  id: passedId,
  onBlur: passedOnBlur = noop,
  rawProps: passedRawProps = {},
  onChange: passedOnChange,
  valueFormatter,
  ...inputProps
}: FormInputProps<T>) => {
  const generatedId = useId();
  // Shared with `FormComponentWrapper`'s label `htmlFor` so the rendered
  // label and input are actually associated (a11y + RTL `getByLabelText`).
  const inputId = passedId ?? generatedId;

  return (
    <Controller
      name={name}
      {...(control
        ? { control }
        : {
            // if undefined, Controller reads from FormProvider
          })}
      rules={rules}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => {
        const handleOnBlur = () => {
          onBlur();
          passedOnBlur?.({} as React.FocusEvent<HTMLInputElement>);
        };

        const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = event.target.value;
          const formattedValue = valueFormatter
            ? valueFormatter(rawValue)
            : rawValue;

          passedOnChange?.(rawValue, formattedValue);
          onChange(formattedValue);
        };

        const handleOnClear: React.MouseEventHandler<HTMLButtonElement> = (
          _,
        ) => {
          passedOnChange?.('', '');
          onChange('');
        };

        const rawPropsWithDataName = {
          ...passedRawProps,
          'data-name': name,
          ...(maxLength === undefined ? {} : { maxLength }),
        };

        const displayMessage =
          maxLength === undefined ? (
            message
          ) : (
            // Single footer row: help text on the left, counter pinned right by
            // `ml-auto` so it stays right-aligned with no help text (CEXP-23596).
            <div className="gap-spacing-ref-default flex">
              {message ? <span className="min-w-0">{message}</span> : null}
              <span className="ml-auto shrink-0">
                {(value ?? '').length}/{maxLength}
              </span>
            </div>
          );

        return (
          <FormComponentWrapper
            name={name}
            id={inputId}
            description={description}
            label={label}
            error={hideError ? undefined : error}
            className={wrapperClassname}
            message={displayMessage}
            optional={optional}
            disabled={disabled}
          >
            {/* Value needs to be set as an empty string instead of undefined since if it is it will be treated as an uncontrolled input */}
            <Input
              id={inputId}
              disabled={disabled}
              value={value ?? ''}
              data-name={name}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              onClear={handleOnClear}
              error={!!error}
              rawProps={rawPropsWithDataName}
              {...inputProps}
            />
          </FormComponentWrapper>
        );
      }}
    />
  );
};
